/**
 * The response chart's two scales: where the time labels go and how tall the
 * y axis is. Pure, like `lib/monitoringSeries.ts`, so both rules are tested
 * without drawing anything.
 *
 * Why neither is left to recharts:
 *
 *  - Recharts spaces numeric ticks by round numbers of MILLISECONDS, which on
 *    a time axis means labels at 14:03:27. These ticks fall on the local
 *    clock (whole hours, local midnights, Mondays), so they stay put on the
 *    time they name and simply slide as the chart scrolls instead of being
 *    recomputed into new odd minutes every frame.
 *  - The y axis is scaled to the median line, not to the highest value on
 *    screen. One eight-second timeout among two hundred 300 ms answers would
 *    otherwise squash the line the reader came to look at into the bottom
 *    few pixels. The same goes for the band: a half-hour bucket holds six
 *    checks, so its p95 IS its slowest check, and topping the axis at the
 *    highest bucket p95 flattened the line exactly like a raw spike did. The
 *    band may lift the top to twice the line's, never further. Anything
 *    above is clipped at the edge, and the tooltip and the "slowest" figure
 *    still give its real value.
 */
import { DAY_MS, HOUR_MS, MINUTE_MS, pointX, type MonitorSeriesPoint } from "../../../../lib/monitoring";

// ── Time ticks ───────────────────────────────────────────────────────────────

/** Tick intervals, finest first. Past a day they are whole local days. */
const TICK_STEPS: readonly number[] = [
  5 * MINUTE_MS,
  10 * MINUTE_MS,
  15 * MINUTE_MS,
  30 * MINUTE_MS,
  HOUR_MS,
  2 * HOUR_MS,
  3 * HOUR_MS,
  6 * HOUR_MS,
  12 * HOUR_MS,
  DAY_MS,
  2 * DAY_MS,
  7 * DAY_MS,
  14 * DAY_MS,
];

/** A label needs about this much room: "25.9" or "14:00" plus air on both sides. */
export const MIN_TICK_GAP_PX = 72;
/** A label closer than this to either end of the plot is dropped rather than drawn half over the y axis. */
const EDGE_PX = 14;
/** Enough for any window the chart can show; only a broken input could reach it. */
const MAX_TICKS = 200;

const offsetMs = (t: number) => new Date(t).getTimezoneOffset() * MINUTE_MS;
/** The local wall clock as if it were UTC, so local hours and days align with plain arithmetic. */
const wall = (t: number) => t - offsetMs(t);
/** Back from the wall clock to an instant; the offset is read twice so a DST change is met. */
const instant = (w: number) => w + offsetMs(w + offsetMs(w));

/**
 * Tick positions inside [from, to) for a plot this wide: the finest interval
 * that leaves each label MIN_TICK_GAP_PX, aligned to the local clock. Week
 * and fortnight ticks fall on Mondays.
 */
export function timeTicks(from: number, to: number, plotWidth: number): number[] {
  if (!Number.isFinite(from) || !Number.isFinite(to) || !(to > from) || !(plotWidth > 0)) return [];
  const room = Math.max(1, Math.floor(plotWidth / MIN_TICK_GAP_PX));
  const step = TICK_STEPS.find((s) => (to - from) / s <= room) ?? TICK_STEPS[TICK_STEPS.length - 1]!;
  const out: number[] = [];
  const push = (t: number) => {
    // A DST fall-back maps two wall hours to one instant; keep the first.
    if (t >= from && t > (out[out.length - 1] ?? -Infinity)) out.push(t);
  };
  if (step < DAY_MS) {
    for (let w = Math.ceil(wall(from) / step) * step; out.length < MAX_TICKS; w += step) {
      const t = instant(w);
      if (t >= to) break;
      push(t);
    }
  } else {
    const days = step / DAY_MS;
    // Day 0 of the epoch was a Thursday, so a shift of 3 puts week ticks on Mondays.
    const shift = days >= 7 ? 3 : 0;
    for (let d = Math.floor(wall(from) / DAY_MS); out.length < MAX_TICKS; d += 1) {
      if ((d + shift) % days !== 0) continue;
      const t = instant(d * DAY_MS);
      if (t >= to) break;
      push(t);
    }
  }
  const px = plotWidth / (to - from);
  return out.filter((t) => (t - from) * px >= EDGE_PX && (to - t) * px >= EDGE_PX);
}

// ── The y axis ───────────────────────────────────────────────────────────────

export interface YScale {
  /** The axis' top, ms: a whole number of tick intervals. */
  top: number;
  /** 0 and every interval up to `top`. */
  ticks: number[];
}

/** The y axis when nothing in view answered: a second, so the empty frame still reads as a time scale. */
const EMPTY_TOP_MS = 1000;
/** Tick intervals are 1, 2, 2.5 or 5 times a power of ten. */
const NICE: readonly number[] = [1, 2, 2.5, 5, 10];
/** About this many intervals from 0 to the top. */
const INTERVALS = 4;
/** Room over the highest value, so the line does not graze the frame's top edge. */
const HEADROOM = 1.05;
/** The line's maximum is used as it is while it stays within this much of the line's p95. */
const SPIKE = 1.5;
/** The band may raise the top to this many times the line's, no further. */
const BAND_REACH = 2;

function niceInterval(raw: number): number {
  const mag = 10 ** Math.floor(Math.log10(raw));
  return (NICE.find((n) => n * mag >= raw) ?? 10) * mag;
}

/** The highest value, unless it is a spike more than SPIKE times the p95 of the values; then the p95. */
function guardedMax(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const max = sorted[sorted.length - 1]!;
  const p95 = sorted[Math.max(0, Math.ceil(sorted.length * 0.95) - 1)]!;
  return max <= p95 * SPIKE ? max : p95;
}

/**
 * The y axis for the points drawn in [from, to) (by `pointX`, so the
 * neighbours `viewSeries` adds do not move it): the median line's guarded
 * maximum, raised by the band (buckets' p95) to at most BAND_REACH times
 * that.
 */
export function yScale(points: readonly MonitorSeriesPoint[], step: number, from: number, to: number): YScale {
  const line: number[] = [];
  let band = 0;
  for (const p of points) {
    const x = pointX(p, step);
    if (x < from || x >= to) continue;
    if (p.p50 !== null && Number.isFinite(p.p50)) line.push(p.p50);
    if (step > 0 && p.p95 !== null && Number.isFinite(p.p95)) band = Math.max(band, p.p95);
  }
  const lineHigh = guardedMax(line);
  let high = lineHigh === null ? EMPTY_TOP_MS : Math.max(lineHigh, Math.min(band, BAND_REACH * lineHigh));
  high = Math.max(1, high) * HEADROOM;
  const interval = niceInterval(high / INTERVALS);
  const count = Math.max(1, Math.ceil(high / interval));
  const ticks = Array.from({ length: count + 1 }, (_, i) => Math.round(i * interval * 1000) / 1000);
  return { top: ticks[ticks.length - 1]!, ticks };
}
